import { useState, useCallback, useEffect, useRef } from 'react';
import { useCalculatorStore } from '@/store/calculator';
import { TargetSelector } from './TargetSelector';
import { ViewModeSelector } from './ViewModeSelector';
import { MultiSelectDropdown } from './MultiSelectDropdown';
import { ResultsGrid } from './ResultsGrid';
import { SingleResult } from './SingleResult';
import { KPIHighlights } from './KPIHighlights';
import { ResultSummary } from './ResultSummary';
import { FinancialTimeline } from './FinancialTimeline';
import { EditInputsModal } from './EditInputsModal';
import { StepEditorModal } from './StepEditorModal';
import { FutureDateModal } from './FutureDateModal';
import { DebtServiceRatioSlider } from './DebtServiceRatioSlider';
import { VariableType, TargetType, KPIResult } from '@/lib/calculator';
import { CalculatorEngine } from '@/lib/calculator';
import { Toast } from '../ui/Toast';
import { addMonths } from 'date-fns';

interface CalculatorProps {
  defaultTarget?: TargetType;
}

export function Calculator({ defaultTarget }: CalculatorProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [stepEditorOpen, setStepEditorOpen] = useState(false);
  const [editingVariable, setEditingVariable] = useState<VariableType | null>(null);
  const [isFutureDateModalOpen, setIsFutureDateModalOpen] = useState(false);
  const calculatorEngine = useRef(new CalculatorEngine()).current;
  const initialTargetSetRef = useRef(false);
  
  const { 
    target,
    viewMode, 
    rowVariables, 
    columnVariables, 
    values,
    settings,
    showToast,
    toastMessage,
    setTarget,
    setViewMode,
    addRowVariable,
    removeRowVariable,
    addColumnVariable,
    removeColumnVariable,
    setResults,
    hideToast,
    results,
    selectedCell,
    setSelectedCell
  } = useCalculatorStore();
  
  // Calculate the minimum recommended date (one month from the default first day of next month)
  const nextMonthFirstDay = addMonths(new Date(new Date().getFullYear(), new Date().getMonth(), 1), 1);
  const minRecommendedDate = addMonths(nextMonthFirstDay, 1).getTime();
  
  // Store direct calculation result for snapshot mode
  const [directResult, setDirectResult] = useState<KPIResult | null>(null);
  
  // Set the initial target if defaultTarget is provided and it hasn't been set yet
  useEffect(() => {
    if (defaultTarget && !initialTargetSetRef.current) {
      handleTargetChange(defaultTarget); // Use custom handler instead of direct setTarget
      initialTargetSetRef.current = true;
    }
  }, [defaultTarget]);
  
  // Ensure Power Mode has correct variable distribution on page load
  useEffect(() => {
    // Only run this once on initial load
    if (viewMode === 'power' && columnVariables.length === 0 && rowVariables.length > 1) {
      // Move the last row variable to columns
      const lastVar = rowVariables[rowVariables.length - 1];
      
      // Use setTimeout to ensure we don't trigger state updates too early in initialization
      setTimeout(() => {
        removeRowVariable(rowVariables.length - 1);
        addColumnVariable(lastVar);
      }, 0);
    }
  }, []); // Empty dependency array ensures this runs only once
  
  // Calculate direct result for snapshot mode
  useEffect(() => {
    if (viewMode === 'snapshot') {
      try {
        // Use the direct calculation method for snapshot mode
        const result = calculatorEngine.calculateDirectResult(
          target,
          values,
          settings
        );
        setDirectResult(result);
      } catch (error) {
        setDirectResult(null);
      }
    }
  }, [viewMode, target, values, settings, calculatorEngine]);
  
  // Generate results for compare/power modes whenever inputs change
  useEffect(() => {
    // Only generate grid results if not in snapshot mode and we have variables to work with
    if (viewMode !== 'snapshot' && rowVariables.length > 0) {
      const newResults = calculatorEngine.generateResults(
        target,
        rowVariables.map(type => values[type]),
        columnVariables.map(type => values[type]),
        values,
        settings
      );
      
      setResults(newResults);
      
      // If no cell is selected but results are available, select a default cell
      if ((!selectedCell || !newResults[selectedCell?.row]?.[selectedCell?.col]) && 
          newResults.length > 0 && newResults[0].length > 0) {
        const middleRow = Math.floor(newResults.length / 2);
        const middleCol = Math.floor(newResults[0].length / 2);
        setSelectedCell({ row: middleRow, col: middleCol });
      }
    }
  }, [
    viewMode,
    target, 
    rowVariables, 
    columnVariables, 
    values, 
    settings, 
    calculatorEngine, 
    setResults, 
    selectedCell, 
    setSelectedCell
  ]);

  // Custom handler for target changes
  const handleTargetChange = useCallback((newTarget: TargetType) => {
    // If the new target is 'savings', check if the current date is far enough in the future
    if (newTarget === 'savings') {
      const currentDate = values['date'].value;
      
      // If the date is not at least one month beyond the default next month (i.e., at least 2 months out)
      if (currentDate < minRecommendedDate) {
        // Open the date modal instead of immediately setting the target
        setIsFutureDateModalOpen(true);
        return;
      }
    }
    
    // Otherwise, proceed with the normal target change
    setTarget(newTarget);
  }, [values, minRecommendedDate, setTarget]);
  
  // Handler for when the user completes the future date modal
  const handleFutureDateModalClose = useCallback(() => {
    setIsFutureDateModalOpen(false);
    // Now set the target to savings since the date has been updated
    setTarget('savings');
  }, [setTarget]);

  // Calculate step based on index and direction
  const calculateStepValue = (variable: VariableType, index: number): number => {
    const variableData = values[variable];
    const stepDirection = variableData.stepDirection || 'both';
    let stepMultiplier = 0;
    
    if (stepDirection === 'both') {
      // Standard step calculation: -1, 0, 1
      stepMultiplier = index - 1;
    } else if (stepDirection === 'up') {
      // For 'up' direction: 0, 1, 2
      stepMultiplier = index;
    } else if (stepDirection === 'down') {
      // For 'down' direction: -2, -1, 0
      stepMultiplier = index - 2;
    }
    
    // Calculate value with step multiplier
    const valueAtStep = values[variable].value + (stepMultiplier * values[variable].step);
    
    // Ensure value is not negative
    return Math.max(0, valueAtStep);
  };

  // Get value for a variable, considering steps in grid mode
  const getValue = (type: VariableType | 'amortizationYears' | 'debtServiceRatio'): number => {
    // Handle special cases for settings
    if (type === 'amortizationYears') {
      return settings.amortizationYears;
    }

    if (type === 'debtServiceRatio') {
      return settings.debtServiceRatio * 100; // Convert to percentage for display
    }
    
    // In snapshot mode, always use actual values
    if (viewMode === 'snapshot') {
      return values[type as VariableType].value;
    }
    
    // For grid/compare modes, adjust values based on selected cell
    if (selectedCell) {
      // Check if it's a row variable
      const rowVarIndex = rowVariables.indexOf(type as VariableType);
      if (rowVarIndex !== -1) {
        return calculateStepValue(type as VariableType, selectedCell.row);
      }
      
      // Check if it's a column variable
      const colVarIndex = columnVariables.indexOf(type as VariableType);
      if (colVarIndex !== -1) {
        return calculateStepValue(type as VariableType, selectedCell.col);
      }
    }
    
    // Default to base value if not a selected variable
    return values[type as VariableType].value;
  };

  // Handler for opening the edit inputs modal
  const handleOpenEditInputs = () => {
    setIsModalOpen(true);
  };

  // Handler for opening the step editor modal
  const handleOpenStepEditor = (varType: VariableType) => {
    setEditingVariable(varType);
    setStepEditorOpen(true);
  };

  // Handler for closing the step editor modal
  const handleCloseStepEditor = () => {
    setStepEditorOpen(false);
    setEditingVariable(null);
  };

  // Get the selected result based on view mode
  const selectedResult = viewMode === 'snapshot' 
    ? directResult 
    : (selectedCell && results.length > 0 ? results[selectedCell.row]?.[selectedCell?.col] : null);

  return (
    <div className="w-full space-y-2">
      <TargetSelector 
        selected={target}
        onSelect={handleTargetChange} // Use our custom handler
        onOpenEditInputs={handleOpenEditInputs}
      />

      {/* Results Section */}
      <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm border border-gray-200 mt-3">
        <h2 className="text-lg font-semibold mb-1">Results</h2>
        
        {/* View Mode Selector */}
        <ViewModeSelector 
          currentMode={viewMode}
          onChange={setViewMode}
        />
        
        {/* Comparison Variables for Compare/Power mode */}
        {viewMode === 'compare' && (
          <div className="mt-3 mb-0">
            <MultiSelectDropdown
              label="Compare scenarios by varying:"
              selectedVariables={rowVariables}
              excludeVariables={[target as unknown as VariableType]}
              onAddVariable={addRowVariable}
              onRemoveVariable={removeRowVariable}
              onEditStep={handleOpenStepEditor}
            />
          </div>
        )}
        
        {viewMode === 'power' && (
          <div className="mt-3 mb-0 grid grid-cols-1 md:grid-cols-2 gap-2">
            <MultiSelectDropdown
              label="Primary variables (rows):"
              selectedVariables={rowVariables}
              excludeVariables={[target as unknown as VariableType, ...columnVariables]}
              onAddVariable={addRowVariable}
              onRemoveVariable={removeRowVariable}
              onEditStep={handleOpenStepEditor}
            />
            <MultiSelectDropdown
              label="Secondary variables (columns):"
              selectedVariables={columnVariables}
              excludeVariables={[target as unknown as VariableType, ...rowVariables]}
              onAddVariable={addColumnVariable}
              onRemoveVariable={removeColumnVariable}
              onEditStep={handleOpenStepEditor}
            />
          </div>
        )}
                
        {/* Results Grid or Single Result */}
        <div className="mt-1">
          {viewMode === 'snapshot' ? (
            // For snapshot view, use the direct result
            directResult ? (
              <SingleResult 
                result={directResult}
                target={target}
                annualIncome={values['income'].value}
              />
            ) : (
              <div className="p-4 text-center bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-gray-600">Calculating your result...</p>
              </div>
            )
          ) : (
            // For grid and compare modes, use the results grid
            <ResultsGrid 
              results={results}
              rowVariables={rowVariables.map(type => values[type])}
              columnVariables={columnVariables.map(type => values[type])}
              target={target}
              selectedCell={selectedCell}
              onCellSelect={setSelectedCell}
              onStepEdit={handleOpenStepEditor}
            />
          )}
        </div>
      </div>
      
      {/* Debt Service Ratio Slider - Now outside the results box */}
      {selectedResult && target !== 'payment' && (
        <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm border border-gray-200 mt-3">
          <DebtServiceRatioSlider />
        </div>
      )}
      
      {/* KPI Highlights Component */}
      {selectedResult && (
        <div className="mt-3">
          <KPIHighlights
            key={`highlights-${target}-${viewMode}`}  
            price={selectedResult.maxPrice || 0}
            purchaseDate={
              target === 'date'
                ? (selectedResult.purchaseDate || new Date(getValue('date')))
                : new Date(getValue('date'))
            }
            monthlyPayment={selectedResult.monthlyPayment || 0}
            interestRate={selectedResult.interestRate || 0}
            amortizationYears={settings.amortizationYears}
            downPayment={selectedResult.downPayment || 0}
            initialDownPayment={selectedResult.initialDownPayment || 0}
            additionalSavings={selectedResult.additionalSavings || 0}
            mortgageAmount={selectedResult.mortgageAmount || 0}
            totalInterestPaid={selectedResult.totalInterestPaid || 0}
            monthlyIncome={getValue('income') / 12}
            savingPeriodMonths={selectedResult.savingPeriodMonths || 0}
          />
        </div>
      )}

      {/* Financial Timeline */}
      {selectedResult && (
        <div className="mt-3">
          <FinancialTimeline
            key={`timeline-${viewMode === 'snapshot' ? 'snapshot' : `${selectedCell?.row}-${selectedCell?.col}`}`}
            currentDate={new Date()}
            purchaseDate={
              target === 'date'
                ? (selectedResult.purchaseDate || new Date(getValue('date')))
                : new Date(getValue('date'))
            }
            monthlySavings={
              target === 'savings'
                ? selectedResult.requiredSavings || 0
                : getValue('savings')
            }
            initialDownPayment={selectedResult.initialDownPayment || 0}
            mortgageAmount={selectedResult.mortgageAmount || 0}
            monthlyMortgagePayment={selectedResult.monthlyPayment || 0}
            interestRate={selectedResult.interestRate || 0}
            amortizationYears={settings.amortizationYears}
            finalDownPayment={selectedResult.downPayment || 0}
          />
        </div>
      )}
      
      {/* Result Summary */}
      {selectedResult && (
        <div className="mt-3">
          <ResultSummary
            selectedResult={selectedResult}
            target={target}
            getValue={getValue}
          />
        </div>
      )}

      {/* Edit Inputs Modal */}
      <EditInputsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />

      {/* Step Editor Modal */}
      <StepEditorModal
        isOpen={stepEditorOpen}
        onClose={handleCloseStepEditor}
        variableType={editingVariable}
      />

      {/* Future Date Modal */}
      <FutureDateModal
        isOpen={isFutureDateModalOpen}
        onClose={handleFutureDateModalClose}
        defaultDate={values['date'].value}
      />

      {/* Toast Notification */}
      {showToast && (
        <Toast
          message={toastMessage}
          onClose={hideToast}
        />
      )}
    </div>
  );
}